import { arrayOf, shape, string, number, bool, oneOf, func } from 'prop-types';
import React from 'react';
import ReactDOMServer from "react-dom/server";

import { orderBy, isEqual } from 'lodash';

import { ReactAmChart } from '../../ReactAmChart';
import CompilationTooltip from './CompilationTooltip';
import styles from './styles.scss';

const FADE_OPACITY = 0.15;
const TRANSITION_DURATION = 500;

export default class DaypartCompilationChart extends ReactAmChart {
  static propTypes = {
    data: arrayOf(shape({
      time: string,
      baseValue: number,
      additionalValue: number,
      guidelineValue: number
    })),
    maxAnimationEntries: number,
    maxColWidth: number,
    axisFontSize: number,
    axisFontWeight: number,
    axisFontColor: string,
    labelFontSize: number,
    labelFontWeight: number,
    labelFontColor: string,
    baseColor: string,
    additionColor: string,
    guidelineColor: string,
    baseLabel: string,
    guidelineLabel: string,
    description: string,
    fontFamily: string,
    tooltipHeader: string,
    tooltipHeaderKey: string,
    totalValueTooltipLabel: string,
    baseValueTooltipLabel: string,
    additionalValueTooltipLabel: string,
    guidelineValueTooltipLabel: string,
    showBaseValues: bool,
    showAdditionalValues: bool,
    showGuidelineValues: bool,
    showBaseOption: bool,
    highlightValues: oneOf(['none', 'base', 'addition', 'guideline']),
    cutOverDate: string,
    cutOverDateTooltip: string,
    cutOverColor: string,
    cutOverBackgroundColor: string,
  }
  static defaultProps = {
    data: [],
    maxAnimationEntries: 70,
    maxColWidth: 40,
    baseColor: '#00BCD4',
    additionColor: '#3F51B5',
    guidelineColor: '#CF87DF',
    baseLabel: '',
    guidelineLabel: '',
    description: '',
    totalValueTooltipLabel: '',
    baseValueTooltipLabel: '',
    additionalValueTooltipLabel: '',
    guidelineValueTooltipLabel: '',
    highlightValues: 'none',
    showBaseValues: true,
    showBaseOption: true,
    showAdditionalValues: true,
    showGuidelineValues: true,
    axisFontSize: 12,
    axisFontWeight: 400,
    axisFontColor: 'rgba(0, 0, 0, 0.54)',
    labelFontSize: 14,
    labelFontWeight: 500,
    labelFontColor: 'rgba(0, 0, 0, 0.54)',
    fontFamily: 'Roboto, "Helvetica Neue", sans-serif',
    cutOverColor: '#335B89',
    cutOverBackgroundColor: '#2A323C',
  }

  valueAxis;
  categoryAxis;
  guidelineAxis;
  guidelineSeries;
  baseValueSeries;
  additionalValueSeries;
  singleAdditionalValueSeries;
  prevTooltipInfo = {};
  currentVisibilityInfo = {};
  cutOverSeries;
  drawFunction(chartId, prevChart, am4core, am4charts) {
    const {
      data,
      maxAnimationEntries,
      maxColWidth,
      labelFontSize,
      labelFontWeight,
      labelFontColor,
      fontFamily,
      baseColor,
      additionColor,
      guidelineColor,
      guidelineLabel,
      highlightValues,
      showBaseValues,
      showAdditionalValues,
      showGuidelineValues,
      description,
      tooltipHeader,
      tooltipHeaderKey,
      totalValueTooltipLabel,
      baseValueTooltipLabel,
      additionalValueTooltipLabel,
      guidelineValueTooltipLabel,
      showBaseOption,
      yAxisLabel,
      cutOverDate,
      cutOverDateTooltip,
      cutOverColor,
      cutOverBackgroundColor,
    } = this.props;

    //--------------------------------------------------------------------------------
    //------------------------------Setup Chart Elements------------------------------
    //--------------------------------------------------------------------------------
    const chart = prevChart || am4core.create(chartId, am4charts.XYChart);

    if (!prevChart) {
      //Setup chart cursor if this is a new chart
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.behavior = 'none';
      chart.cursor.lineX.strokeWidth = 0;
      chart.cursor.lineY.strokeWidth = 0;
      chart.cursor.lineX.exportable = false;
      chart.cursor.lineY.exportable = false;
      chart.zoomOutButton.disabled = true;
    }

    if (!this.categoryAxis) {
      //Create Category Axis (X axis)
      this.categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      //this.categoryAxis.events.on("sizechanged", this.handleAxisSizeChanged)
      this.categoryAxis.fontFamily = fontFamily;
      this.categoryAxis.tooltip.disabled = true;
      this.categoryAxis.dataFields.category = 'time';
      this.categoryAxis.renderer.labels.template.maxWidth = 90;
      this.categoryAxis.renderer.minGridDistance = 50;
      this.categoryAxis.renderer.grid.template.location = 0;
      this.categoryAxis.renderer.grid.template.adapter.add('visible', function(value, target) {
        return (target.dataItem.index === 0 || target.dataItem.index === -1);
      });

      this.setAxisFont(this.categoryAxis);
    }
    if (!this.cutOverSeries) {
      // Create cut over line
      this.cutOverSeries = this.categoryAxis.axisRanges.create();
      this.cutOverSeries.category = cutOverDate;
      this.cutOverSeries.grid.stroke = am4core.color(cutOverColor);
      this.cutOverSeries.grid.strokeWidth = 4;
      this.cutOverSeries.grid.strokeOpacity = 1;
      this.cutOverSeries.grid.above = true;
      this.cutOverSeries.locations.category = 0.5;

      const tooltipSourceUpdated = new am4core.Tooltip();
      tooltipSourceUpdated.properties.pointerOrientation = 'left';
      tooltipSourceUpdated.getFillFromObject = false;
      tooltipSourceUpdated.getStrokeFromObject = true;
      tooltipSourceUpdated.background.fill = am4core.color(cutOverBackgroundColor);

      this.cutOverSeries.axisFill.tooltip = tooltipSourceUpdated;
      this.cutOverSeries.axisFill.tooltipHTML = ReactDOMServer.renderToString(
        <div className={styles.tooltipCustom}>{cutOverDateTooltip}</div>
      );
      this.cutOverSeries.axisFill.interactionsEnabled = true;
      this.cutOverSeries.axisFill.isMeasured = true;
      this.cutOverSeries.axisFill.tooltipY = am4core.percent(16);
      this.cutOverSeries.axisFill.tooltipX = am4core.percent(0);
      this.cutOverSeries.label.disabled = true;
    }


    if (!this.valueAxis) {
      //Create Value Axis (left most Y Axis) for base & additional values - bar chart
      this.valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      this.valueAxis.min = 0;
      this.valueAxis.fontFamily = fontFamily;
      this.valueAxis.cursorTooltipEnabled = false;
      this.valueAxis.title.fill = labelFontColor;
      this.valueAxis.title.fontSize = labelFontSize;
      this.valueAxis.title.fontWeight = labelFontWeight;
      this.valueAxis.title.fontFamily = fontFamily;
      this.setAxisFont(this.valueAxis);
    }

    if (!this.guidelineAxis) {
      //Create Guideline Axis (right most Y Axis) for guideline values - line chart
      this.guidelineAxis = chart.yAxes.push(new am4charts.ValueAxis());
      this.guidelineAxis.min = 0;
      this.guidelineAxis.fontFamily = fontFamily;
      this.guidelineAxis.renderer.opposite = true;
      this.guidelineAxis.cursorTooltipEnabled = false;
      this.guidelineAxis.renderer.grid.template.disabled = true;
      this.guidelineAxis.title.fill = labelFontColor;
      this.guidelineAxis.title.fontSize = labelFontSize;
      this.guidelineAxis.title.fontWeight = labelFontWeight;
      this.guidelineAxis.title.fontFamily = fontFamily;
      this.setAxisFont(this.guidelineAxis);
    }

    if (!this.baseValueSeries) {
      this.baseValueSeries = chart.series.push(new am4charts.ColumnSeries());
      this.baseValueSeries.dataFields.categoryX = 'time';
      this.baseValueSeries.dataFields.valueY = 'baseValue';
      this.baseValueSeries.strokeWidth = 0;
      this.baseValueSeries.fill = am4core.color(baseColor);
      this.baseValueSeries.columns.template.maxWidth = maxColWidth;
      this.baseValueSeries.sequencedInterpolation = true;
      this.baseValueSeries.hiddenState.transitionDuration = TRANSITION_DURATION;
      this.baseValueSeries.defaultState.transitionDuration = TRANSITION_DURATION;

      this.baseValueSeries.hidden = !showBaseValues;
      this.currentVisibilityInfo.showBaseValues = showBaseValues;

      this.baseValueSeries.tooltip.getFillFromObject = false;
      this.baseValueSeries.tooltip.background.fill = am4core.color('#FFFFFF');
      this.baseValueSeries.tooltip.tooltipContainer.exportable = false;
    }

    if (!this.additionalValueSeries) {
      this.additionalValueSeries = chart.series.push(new am4charts.ColumnSeries());
      this.additionalValueSeries.stacked = true;
      this.additionalValueSeries.dataFields.categoryX = 'time';
      this.additionalValueSeries.dataFields.valueY = 'additionalValue';
      this.additionalValueSeries.strokeWidth = 0;
      this.additionalValueSeries.fill = am4core.color(additionColor);
      this.additionalValueSeries.columns.template.maxWidth = maxColWidth;
      this.additionalValueSeries.sequencedInterpolation = true;
      this.additionalValueSeries.defaultState.transitionDuration = 0;
      this.additionalValueSeries.hiddenState.transitionDuration = TRANSITION_DURATION;

      this.additionalValueSeries.tooltip.getFillFromObject = false;
      this.additionalValueSeries.tooltip.background.fill = am4core.color('#FFFFFF');

      this.additionalValueSeries.hidden = !showAdditionalValues;
      this.currentVisibilityInfo.showAdditionalValues = showAdditionalValues;

      this.singleAdditionalValueSeries = chart.series.push(new am4charts.ColumnSeries());
      this.singleAdditionalValueSeries.clustered = false;
      this.singleAdditionalValueSeries.dataFields.categoryX = 'time';
      this.singleAdditionalValueSeries.dataFields.valueY = 'additionalValue';
      this.singleAdditionalValueSeries.strokeWidth = 0;
      this.singleAdditionalValueSeries.fill = am4core.color(additionColor);
      this.singleAdditionalValueSeries.columns.template.maxWidth = maxColWidth;
      this.singleAdditionalValueSeries.sequencedInterpolation = true;
      this.singleAdditionalValueSeries.hiddenState.transitionDuration = TRANSITION_DURATION;
      this.singleAdditionalValueSeries.defaultState.transitionDuration = TRANSITION_DURATION;

      this.singleAdditionalValueSeries.opacity = 0;
      this.singleAdditionalValueSeries.hidden = true;
    }

    if (!this.guidelineSeries) {
      this.guidelineSeries = chart.series.push(new am4charts.LineSeries());
      this.guidelineSeries.tensionX = 0.8;
      this.guidelineSeries.fillOpacity = 0.3;
      this.guidelineSeries.yAxis = this.guidelineAxis;
      this.guidelineSeries.dataFields.categoryX = 'time';
      this.guidelineSeries.dataFields.valueY = 'guidelineValue';
      this.guidelineSeries.fill = am4core.color(guidelineColor);
      this.guidelineSeries.sequencedInterpolation = true;
      this.guidelineSeries.hiddenState.transitionDuration = TRANSITION_DURATION;
      this.guidelineSeries.defaultState.transitionDuration = TRANSITION_DURATION;

      this.guidelineSeries.tooltip.getFillFromObject = false;
      this.guidelineSeries.tooltip.background.fill = am4core.color('#FFFFFF');

      this.guidelineSeries.hidden = !showGuidelineValues;
      this.currentVisibilityInfo.showGuidelineValues = showGuidelineValues;
    }

    //---------------------------------------------------------------------------------
    //------------------------------Update/Set Chart Data------------------------------
    //---------------------------------------------------------------------------------
    const newDescription = this.prevTooltipInfo.description !== description;
    const newTotalLabel = this.prevTooltipInfo.totalLabel !== totalValueTooltipLabel;
    const newBaseLabel = this.prevTooltipInfo.baseLabel !== baseValueTooltipLabel;
    const newAdditionalLabel = this.prevTooltipInfo.additionalLabel !== additionalValueTooltipLabel;
    const newGuidelineLabel = this.prevTooltipInfo.guidelineLabel !== guidelineValueTooltipLabel;
    if (newDescription || newTotalLabel || newBaseLabel || newAdditionalLabel || newGuidelineLabel) {
      //Update Tooltip Info
      this.prevTooltipInfo = {
        description,
        totalLabel: totalValueTooltipLabel,
        baseLabel: baseValueTooltipLabel,
        additionalLabel: additionalValueTooltipLabel,
        guidelineLabel: guidelineValueTooltipLabel
      };

      const htmlTooltip = ReactDOMServer.renderToString(
        <CompilationTooltip
          header={tooltipHeader}
          headerKey={!tooltipHeader ? tooltipHeaderKey : null}
          subheader={description}
          totalValueKey='totalValue'
          totalValueLabel={totalValueTooltipLabel}
          baseValueKey='baseValue'
          baseIndicatorColor={baseColor}
          baseValueLabel={baseValueTooltipLabel}
          additionalValueKey='additionalValue'
          additionalIndicatorColor={additionColor}
          additionalValueLabel={additionalValueTooltipLabel}
          guidelineValueKey='guidelineValue'
          guidelineIndicatorColor={guidelineColor}
          guidelineValueLabel={guidelineValueTooltipLabel}
          showBaseOption={showBaseOption}
        />
      );

      //Update Tooltip
      this.guidelineSeries.tooltipHTML = htmlTooltip;
      this.baseValueSeries.tooltipHTML = htmlTooltip;
      this.additionalValueSeries.tooltipHTML = htmlTooltip;
      this.updateTooltipVisibility();
    }

    const newHighlight = this.currentVisibilityInfo.highlightValues !== highlightValues;
    const newBaseVisibility = this.currentVisibilityInfo.showBaseValues !== showBaseValues;
    const newAdditionVisibility = this.currentVisibilityInfo.showAdditionalValues !== showAdditionalValues;
    const newGuidelineVisibility = this.currentVisibilityInfo.showGuidelineValues !== showGuidelineValues;
    if (newBaseVisibility) {
      if (showBaseValues) {
        this.baseValueSeries.show();
      } else {
        this.baseValueSeries.hide();
      }
      this.currentVisibilityInfo.showBaseValues = showBaseValues;
      this.updateTooltipVisibility();
    }

    if (newAdditionVisibility) {
      if (showAdditionalValues) {
        this.additionalValueSeries.show();
        this.singleAdditionalValueSeries.show();
        this.additionalValueSeries.opacity = FADE_OPACITY;
      } else {
        this.additionalValueSeries.hide();
        this.singleAdditionalValueSeries.hide();
      }

      this.currentVisibilityInfo.showAdditionalValues = showAdditionalValues;
      this.updateTooltipVisibility();
    }

    if (newGuidelineVisibility) {
      showGuidelineValues && this.guidelineSeries.show();
      !showGuidelineValues && this.guidelineSeries.hide();
      this.currentVisibilityInfo.showGuidelineValues = showGuidelineValues;
      this.updateTooltipVisibility();
    }

    if (newHighlight) {
      switch (highlightValues) {
      case 'base':
        this.baseValueSeries.opacity = 1;
        this.guidelineSeries.opacity = FADE_OPACITY;
        this.additionalValueSeries.opacity = FADE_OPACITY;
        this.singleAdditionalValueSeries.opacity = 0;
        break;

      case 'addition':
        this.guidelineSeries.opacity = FADE_OPACITY;
        this.baseValueSeries.opacity = FADE_OPACITY;
        this.additionalValueSeries.opacity = FADE_OPACITY;
        this.singleAdditionalValueSeries.opacity = 1;
        if(this.singleAdditionalValueSeries.hidden && this.currentVisibilityInfo.showAdditionalValues) {
          const currentAnimationTime = this.singleAdditionalValueSeries.defaultState.transitionDuration;
          this.singleAdditionalValueSeries.defaultState.transitionDuration = 0;                       //Disable show animation
          this.singleAdditionalValueSeries.show();
          this.singleAdditionalValueSeries.defaultState.transitionDuration = currentAnimationTime;    //Re-enable show animation
        }
        break;

      case 'guideline':
        this.guidelineSeries.opacity = 1;
        this.baseValueSeries.opacity = FADE_OPACITY;
        this.additionalValueSeries.opacity = FADE_OPACITY;
        this.singleAdditionalValueSeries.opacity = 0;
        break;

      default:
        this.guidelineSeries.opacity = 1;
        this.baseValueSeries.opacity = 1;
        this.additionalValueSeries.opacity = 1;
        this.singleAdditionalValueSeries.opacity = 0;
      }
      this.currentVisibilityInfo.highlightValues = highlightValues;
    }

    if (this.valueAxis.title.text !== yAxisLabel) {
      this.valueAxis.title.text = yAxisLabel;
    }

    if (this.guidelineAxis.title.text !== guidelineLabel) {
      this.guidelineAxis.title.text = guidelineLabel;
    }

    const prepedData = data.map(entry => ({
      ...entry,
      baseValue: parseFloat(entry.baseValue.toFixed(1)),
      additionalValue: parseFloat(entry.additionalValue.toFixed(1)),
      guidelineValue: parseFloat(entry.guidelineValue.toFixed(1)),
      totalValue: Math.round(entry.baseValue + entry.additionalValue),
    }));
    const sortedNewData = orderBy(prepedData, ['time', 'baseValue', 'additionalValue', 'guidelineValue']);
    const sortedPrevData = orderBy(chart.data || [], ['time', 'baseValue', 'additionalValue', 'guidelineValue']);
    const newData = !isEqual(sortedNewData, sortedPrevData);
    if (newData) {
      //Disable bar chart animations when it has big num of entries for performance purpose.
      const enableAnimation = prepedData.length <= maxAnimationEntries;
      const barchartsTransitionTime = enableAnimation ? TRANSITION_DURATION : 0;

      this.baseValueSeries.showOnInit = enableAnimation;
      this.baseValueSeries.hiddenState.transitionDuration = barchartsTransitionTime;
      this.baseValueSeries.defaultState.transitionDuration = barchartsTransitionTime;

      this.additionalValueSeries.showOnInit = enableAnimation;
      this.additionalValueSeries.hiddenState.transitionDuration = barchartsTransitionTime;
      //amChart has issue with quick show/hide + animation & opacity
      //this.additionalValueSeries.defaultState.transitionDuration = barchartsTransitionTime;

      this.singleAdditionalValueSeries.showOnInit = enableAnimation;
      this.singleAdditionalValueSeries.hiddenState.transitionDuration = barchartsTransitionTime;
      this.singleAdditionalValueSeries.defaultState.transitionDuration = barchartsTransitionTime;


      chart.data = prepedData;
    }

    if (cutOverDate) {
      this.cutOverSeries.category = cutOverDate;
      // fix cut over series is hidden
      this.cutOverSeries.hide();
      this.cutOverSeries.show(100, 100);
    } else {
      this.cutOverSeries.hide();
    }

    return chart;
  }

  setAxisFont(axis){
    const { axisFontSize, axisFontWeight, axisFontColor } = this.props;
    axis.fontSize = axisFontSize;
    axis.fontWeight = axisFontWeight;
    axis.renderer.labels.template.fill = this.am4core.color(axisFontColor);
  }

  /*
  handleAxisSizeChanged = (event) => {
    const axis = this.categoryAxis;
    const cellWidth = axis.pixelWidth / (axis.endIndex - axis.startIndex);
    if (cellWidth < axis.renderer.labels.template.maxWidth) {
      axis.renderer.labels.template.rotation = -45;
      axis.renderer.labels.template.horizontalCenter = "right";
      axis.renderer.labels.template.verticalCenter = "middle";
    }
    else {
      axis.renderer.labels.template.rotation = 0;
      axis.renderer.labels.template.horizontalCenter = "middle";
      axis.renderer.labels.template.verticalCenter = "top";
    }
  }
  */

  /**
   * Pick a proper tooltip base on series display status.
   * Base series' tooltip is picked as default. If base series are hidden, additionalSeries' tooltip is selected then guidelineSeries'
   */
  updateTooltipVisibility () {
    const showBase = this.currentVisibilityInfo.showBaseValues;
    const showAddition = this.currentVisibilityInfo.showAdditionalValues;

    if (showBase) {
      this.guidelineSeries.tooltip.disabled = true;
      this.baseValueSeries.tooltip.disabled = false;
      this.additionalValueSeries.tooltip.disabled = true;
    } else if (showAddition) {
      this.guidelineSeries.tooltip.disabled = true;
      this.baseValueSeries.tooltip.disabled = true;
      this.additionalValueSeries.tooltip.disabled = false;
    } else {
      this.guidelineSeries.tooltip.disabled = false;
      this.baseValueSeries.tooltip.disabled = true;
      this.additionalValueSeries.tooltip.disabled = true;
    }
  }
}
